/**
 * Navigation Configuration
 * =========================
 * This is the authoritative source of truth for sidebar navigation items.
 * 
 * IMPORTANT:
 * - Every nav item MUST have a unique stable `id`
 * - Do NOT dedupe by path - items are unique by id
 * - pageMetadata is NOT required - it only enhances nav (badges/counts)
 * - Nav renders identically even if pageMetadata fails or returns empty
 * 
 * Hub Keys:
 * - 'sales' (Sales Hub)
 * - 'operations' (Operations Hub)
 * - 'marketing' (Marketing Hub)
 */

// ============================================
// SALES HUB (Workspace items only - Board moved to standalone)
// ============================================
export const PIPELINE_NAV_ITEMS = [
  { 
    id: 'sales-leads-projects', 
    label: 'Leads & Projects', 
    path: '/workspace/records?source=sales&mode=leads', 
    icon: 'Users',
    hubKey: 'sales'
  },
];

// ============================================
// OPERATIONS HUB (Production items - Board removed)
// ============================================
export const PRODUCTION_NAV_ITEMS = [
  { 
    id: 'capacity-scheduling', 
    label: 'Capacity & Scheduling', 
    path: '/operations-hub/capacity', 
    icon: 'Calendar',
    hubKey: 'operations'
  },
  { 
    id: 'production-revenue-projections', 
    label: 'Revenue Projections', 
    path: '/operations-hub/revenue-projections', 
    icon: 'TrendingUp',
    hubKey: 'operations'
  },
  { 
    id: 'production-project-map', 
    label: 'Project Map', 
    path: '/operations-hub/project-map', 
    icon: 'MapPin',
    hubKey: 'operations'
  },
  { 
    id: 'production-crew', 
    label: 'Crew Schedule', 
    path: '/operations-hub/crew', 
    icon: 'Users',
    hubKey: 'operations'
  },
  { 
    id: 'production-main', 
    label: 'Production', 
    path: '/operations-hub/production', 
    icon: 'Wrench',
    hubKey: 'operations'
  },
  { 
    id: 'operations-work-orders', 
    label: 'Work Orders', 
    path: '/operations-hub/work-orders', 
    icon: 'ClipboardList', 
    hubKey: 'operations'
  },
];

// ============================================
// MARKETING HUB
// ============================================
export const MARKETING_NAV_ITEMS = [
  { 
    id: 'marketing-tracker', 
    label: 'Marketing Tracker', 
    path: '/marketing-hub/tracker', 
    icon: 'TrendingUp',
    hubKey: 'marketing',
    leadershipOnly: true,  // Only Admin + Leadership roles (CEO, President, Director, etc.)
    badge: 'MVP'           // Show MVP badge since it uses mocked data
  },
  { 
    id: 'ads-manager', 
    label: 'Ads Manager', 
    path: '/marketing-hub/ads-manager', 
    icon: 'Target',
    hubKey: 'marketing',
    leadershipOnly: true,  // Only Admin + Leadership roles
  },
  { 
    id: 'marketing-pacing', 
    label: 'On-Track Pacing', 
    path: '/marketing-hub/pacing', 
    icon: 'BarChart3',
    hubKey: 'marketing'
  },
  { 
    id: 'marketing-check-ins', 
    label: 'Check-Ins', 
    path: '/marketing-hub/check-ins', 
    icon: 'MapPin',
    hubKey: 'marketing'
  },
  { 
    id: 'marketing-content-studio', 
    label: 'Content Studio', 
    path: '/marketing-hub/content-studio', 
    icon: 'PenTool',
    hubKey: 'marketing'
  },
  { 
    id: 'marketing-scheduler', 
    label: 'Scheduler', 
    path: '/marketing-hub/scheduler', 
    icon: 'Calendar',
    hubKey: 'marketing'
  },
  { 
    id: 'marketing-campaigns', 
    label: 'Campaigns', 
    path: '/marketing-hub/campaigns', 
    icon: 'Megaphone',
    hubKey: 'marketing'
  },
  { 
    id: 'marketing-agency', 
    label: 'Agency (SSW)', 
    path: '/marketing-hub/agency', 
    icon: 'Building2',
    hubKey: 'marketing'
  },
  { 
    id: 'marketing-assets', 
    label: 'Assets', 
    path: '/marketing-hub/assets', 
    icon: 'FolderOpen',
    hubKey: 'marketing'
  },
  { 
    id: 'marketing-settings', 
    label: 'Settings', 
    path: '/marketing-hub/settings', 
    icon: 'Settings',
    hubKey: 'marketing',
    adminOnly: true
  },
];

// ============================================
// ALL NAV ITEMS (for validation)
// ============================================
export const ALL_NAV_ITEMS = [
  ...PIPELINE_NAV_ITEMS,
  ...PRODUCTION_NAV_ITEMS,
  ...MARKETING_NAV_ITEMS,
];

// ============================================
// REQUIRED ITEMS (for regression checks)
// Board has been moved to standalone - sales-board no longer required in sales hub
// ============================================
export const REQUIRED_NAV_ITEMS = {
  sales: ['sales-leads-projects'],  // "Leads & Projects" must exist
  operations: ['capacity-scheduling'], // "Capacity & Scheduling" must exist
  marketing: ['marketing-campaigns'],  // "Campaigns" must exist
};

// ============================================
// HUB CONFIGURATION
// ============================================
export const HUB_CONFIG = {
  sales: {
    id: 'sales',
    label: 'Sales Hub',
    icon: 'Trello',
    items: PIPELINE_NAV_ITEMS,
    // Paths that should auto-expand this hub
    matchPaths: ['/sales-hub/', '/workspace/records'],
    // Shared route handling
    sharedRoutes: [],
  },
  operations: {
    id: 'operations',
    label: 'Operations Hub',
    icon: 'Wrench',
    items: PRODUCTION_NAV_ITEMS,
    // Paths that should auto-expand this hub
    matchPaths: ['/operations-hub/', '/production/'],
    // Shared route handling - if on /jobs and lastClickedHub is 'operations', expand
    sharedRoutes: ['/jobs'],
  },
  marketing: {
    id: 'marketing',
    label: 'Marketing',
    icon: 'Megaphone',
    items: MARKETING_NAV_ITEMS,
    // Paths that should auto-expand this hub
    matchPaths: ['/marketing-hub/', '/marketing/'],
    sharedRoutes: [],
  },
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Determine if a nav item is active based on current path
 * Handles query params for shared routes like /jobs?view=sales
 */
export const isNavItemActive = (item, currentPath, currentSearch = '') => {
  const itemPath = item.path;
  const [itemBasePath, itemQuery] = itemPath.split('?');
  const currentBasePath = currentPath.split('?')[0];
  
  // Exact base path match
  if (currentBasePath === itemBasePath) {
    // If item has query params, check them too
    if (itemQuery) {
      const itemParams = new URLSearchParams(itemQuery);
      const currentParams = new URLSearchParams(currentSearch);
      
      // Check if all item params match current params
      for (const [key, value] of itemParams.entries()) {
        if (currentParams.get(key) !== value) {
          return false;
        }
      }
      return true;
    }
    // No query params on item, base path match is enough
    return true;
  }
  
  return false;
};

/**
 * Determine which hub should be expanded based on current path
 * Returns hub key or null
 */
export const getActiveHubForPath = (currentPath, lastClickedHub = null) => {
  const basePath = currentPath.split('?')[0];
  
  // Check each hub's match paths
  for (const [hubKey, hubConfig] of Object.entries(HUB_CONFIG)) {
    // Check direct path matches
    for (const matchPath of hubConfig.matchPaths) {
      if (basePath.startsWith(matchPath) || basePath === matchPath.replace(/\/$/, '')) {
        return hubKey;
      }
    }
    
    // Check shared routes with lastClickedHub disambiguation
    for (const sharedRoute of hubConfig.sharedRoutes) {
      if (basePath.startsWith(sharedRoute)) {
        // If lastClickedHub matches this hub, expand it
        if (lastClickedHub === hubKey) {
          return hubKey;
        }
      }
    }
  }
  
  // Default for shared routes: if on /jobs with no context, default to sales hub
  if (basePath.startsWith('/jobs') && !lastClickedHub) {
    return 'sales';
  }
  
  return null;
};

/**
 * Validate that all required nav items exist
 * Call this in development to catch regressions early
 */
export const validateNavConfig = () => {
  const errors = [];
  
  for (const [hubKey, requiredIds] of Object.entries(REQUIRED_NAV_ITEMS)) {
    const hubConfig = HUB_CONFIG[hubKey];
    if (!hubConfig) {
      errors.push(`Hub "${hubKey}" is missing from HUB_CONFIG`);
      continue;
    }
    
    for (const requiredId of requiredIds) {
      const item = hubConfig.items.find(i => i.id === requiredId);
      if (!item) {
        errors.push(`Required nav item "${requiredId}" is missing from ${hubKey} hub`);
      }
    }
  }
  
  // Validate unique IDs
  const allIds = ALL_NAV_ITEMS.map(i => i.id);
  const duplicateIds = allIds.filter((id, idx) => allIds.indexOf(id) !== idx);
  if (duplicateIds.length > 0) {
    errors.push(`Duplicate nav item IDs found: ${duplicateIds.join(', ')}`);
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Run validation in development mode
 */
if (process.env.NODE_ENV === 'development') {
  const validation = validateNavConfig();
  if (!validation.valid) {
    console.error('🚨 Navigation Config Validation Failed:');
    validation.errors.forEach(err => console.error(`  - ${err}`));
    // Fail loudly in development
    throw new Error(`Navigation config validation failed: ${validation.errors.join('; ')}`);
  } else {
    console.log('✅ Navigation config validated successfully');
  }
}

export default {
  PIPELINE_NAV_ITEMS,
  PRODUCTION_NAV_ITEMS,
  MARKETING_NAV_ITEMS,
  ALL_NAV_ITEMS,
  REQUIRED_NAV_ITEMS,
  HUB_CONFIG,
  isNavItemActive,
  getActiveHubForPath,
  validateNavConfig,
};
